// The <head> tags that make the ADMIN installable as an app.
//
// ⚠️ ADDED AT RUNTIME, ON ADMIN PAGES ONLY — NOT WRITTEN IN index.html.
// index.html is also every customer page (/flow, /preview, /upload…). A static
// manifest link there would let Chrome offer « Install Storiz Admin » to a
// customer in the middle of an order, and an iPhone « Add to Home Screen » from
// a customer page would make an icon that opens the admin login. So:
//   • main.tsx calls applyAdminAppHeadIfAdmin() at boot (a direct /admin load);
//   • AdminLayout calls applyAdminAppHead() on mount (arriving from /login).
// The tags are never removed: going from one admin page to the next must not
// make Chrome re-read the manifest each time.
import { isAdminPath } from './detect';
import { currentMode, themeColorFor, LIGHT_THEME_COLOR } from '@/lib/theme/adminTheme';

/** Storiz gold (lib/brand.ts BRAND.gold — the canonical gold, also the favicon's).
 *  The LIGHT status bar and the manifest; night mode uses its canvas instead. */
export const ADMIN_THEME_COLOR = LIGHT_THEME_COLOR;

/**
 * STATUS BAR IN NIGHT MODE (2026-10-02) — why the style stays « default ».
 * On iOS 15+ an installed web app with 'default' paints its status bar in the
 * page's theme-color and picks dark or light clock text by itself; theme-color
 * is updated live (lib/theme/adminTheme.ts › applyTheme), so dark mode gets a
 * bar in the dark canvas colour, the same as the phone header right under it.
 * 'black-translucent' was rejected: it draws the page under a bar whose text is
 * ALWAYS white — invisible on the light header — and iOS reads the style once
 * per launch (sometimes only when the icon is added), so it cannot follow a
 * switch between light and dark. Trade-off: on iOS 14 and older the bar stays
 * light in dark mode.
 */
const META: Array<[name: string, content: string]> = [
  // iPhone: open full screen from the home-screen icon (Web Push needs that).
  ['apple-mobile-web-app-capable', 'yes'],
  ['mobile-web-app-capable', 'yes'],
  ['apple-mobile-web-app-status-bar-style', 'default'],
];

/**
 * SAFE AREAS (2026-10-01). `viewport-fit=cover` lets the admin draw under the
 * notch and the home bar; AdminLayout then pads its header and bottom tab bar
 * with --adm-safe-top / --adm-safe-bottom (index.css). ADMIN PAGES ONLY:
 * index.html is every customer page too, and they are not built for it — so
 * the shell takes it off again when the person leaves the admin (App.tsx).
 */
const FIT = 'viewport-fit=cover';

export function syncAdminViewport(doc: Document, pathname: string): void {
  const meta = doc.head.querySelector<HTMLMetaElement>('meta[name="viewport"]');
  if (!meta) return;
  const parts = (meta.content || '').split(',').map((p) => p.trim()).filter((p) => p && !/^viewport-fit\s*=/.test(p));
  if (isAdminPath(pathname)) parts.push(FIT);
  const next = parts.join(', ');
  if (meta.content !== next) meta.content = next;
}

export function applyAdminAppHead(doc: Document): void {
  syncAdminViewport(doc, '/admin');
  if (!doc.head.querySelector('link[rel="manifest"]')) {
    const link = doc.createElement('link');
    link.rel = 'manifest';
    link.href = '/manifest.webmanifest';
    doc.head.appendChild(link);
  }
  // theme-color follows the mode the document is ALREADY in (set by the boot
  // script in index.html, then by AdminAppShell) — re-running this on every
  // admin page must not paint a dark app's status bar gold again.
  for (const [name, content] of [['theme-color', themeColorFor(currentMode(doc))], ...META]) {
    let meta = doc.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
    if (!meta) {
      meta = doc.createElement('meta');
      meta.name = name;
      doc.head.appendChild(meta);
    }
    meta.content = content;
  }
}

export function applyAdminAppHeadIfAdmin(doc: Document, pathname: string): void {
  if (isAdminPath(pathname)) applyAdminAppHead(doc);
}
